import axios from 'axios';
import { camelizeKeys, decamelizeKeys } from 'humps';
import { replayRateLimited } from './rateLimit';

// `process.env.REACT_APP_*` is inlined at build time by whichever app's own
// CRA build bundles this file, exactly as it was when this client lived
// under apps/to/src/api (US-025) — the value read here is always the
// bundling app's own env, never TO's specifically.
const CLIENT_ID = process.env.REACT_APP_CLIENT_ID;
const API_NODE_BASE =
  process.env.REACT_APP_API_NODE_BASE_URL ?? process.env.REACT_APP_API_BASE_URL;

const api = axios.create({ baseURL: API_NODE_BASE });

const formatConfig = ({ params, ...opts } = {}) => ({
  ...opts,
  params: decamelizeKeys(params),
});

const isFormData = (val) =>
  typeof FormData !== 'undefined' && val instanceof FormData;

api.interceptors.request.use((config) => {
  const impersonationToken = localStorage.getItem('impersonationToken');
  const accessToken =
    impersonationToken?.length > 0
      ? impersonationToken
      : localStorage.getItem('accessToken');

  config.headers['Accept'] = 'application/json';

  if (Boolean(accessToken)) {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }

  if (!isFormData(config.data)) {
    config.data = camelizeKeys(config.data);
  }

  return config;
});

const formatResponse = (response) => {
  if (!Boolean(response)) {
    return response;
  }

  return camelizeKeys(response);
};

export const post = (uri, payload = {}, config, formatResp = true) =>
  api
    .post(uri, payload, formatConfig(config))
    .then(formatResp ? formatResponse : (response) => response);

const refreshToken = () =>
  post('/api/v1/oauth/token', {
    refreshToken: localStorage.getItem('refreshToken'),
  });

const revokeToken = async (impersonating = false) => {
  await post('/api/v1/oauth/revoke-token', {
    token: impersonating
      ? localStorage.getItem('impersonationToken')
      : localStorage.getItem('accessToken'),
    clientId: CLIENT_ID,
  });
};

// The error body is a plain string on some routes and a { message } object on
// others; read both shapes without assuming either.
const bodyText = (data) =>
  typeof data === 'string' ? data : (data?.message ?? '');

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const replay = replayRateLimited(error, api);
    if (replay) return replay;
    const impersonationToken = localStorage.getItem('impersonationToken');
    const impersonating = impersonationToken?.length > 0;
    if (
      (error.response?.status === 400 &&
        bodyText(error.response?.data).includes('Credentials are invalid.')) ||
      (error.response?.status === 401 &&
        (bodyText(error.response?.data).includes('Unauthorized') ||
          // Newer API builds return a JSON envelope with a stable code.
          error.response?.data?.code === 'AUTHENTICATION_REQUIRED'))
    ) {
      if (impersonating) {
        await revokeToken(true);
        localStorage.setItem('impersonationToken', '');
      } else {
        await revokeToken();
        localStorage.setItem('accessToken', '');
        localStorage.setItem('refreshToken', '');
        localStorage.setItem('impersonationToken', '');
      }
      window.location.reload();
      return;
    }

    const originalRequest = error.config;
    const refreshTokenValue = localStorage.getItem('refreshToken');

    if (!error.response) {
      return Promise.reject(error);
    }

    if (originalRequest.url.indexOf('oauth/token') !== -1) {
      return Promise.reject(error);
    }

    if (error.response.status === 401) {
      if (originalRequest._hasBeenRetried) {
        localStorage.setItem('accessToken', '');
        localStorage.setItem('refreshToken', '');
        localStorage.removeItem('impersonationToken');
      } else {
        if (!refreshTokenValue) {
          localStorage.setItem('accessToken', '');
          localStorage.setItem('refreshToken', '');
          localStorage.removeItem('impersonationToken');
          window.location.reload();
          return Promise.reject(error);
        }

        originalRequest._hasBeenRetried = true;

        return refreshToken().then(
          ({ data }) => {
            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('refreshToken', data.refreshToken);

            originalRequest.headers['Authorization'] =
              `Bearer ${data.accessToken}`;
            return axios(originalRequest);
          },
          () => {
            localStorage.setItem('accessToken', '');
            localStorage.setItem('refreshToken', '');
            localStorage.removeItem('impersonationToken');
          },
        );
      }
    }

    return Promise.reject(error);
  },
);

export const get = (uri, config = {}, formatResp = true) =>
  api
    .get(uri, formatConfig(config))
    .then(formatResp ? formatResponse : (response) => response);
